import { datadogRum } from '@datadog/browser-rum';
import type { PersistedClient, Persister } from '@tanstack/query-persist-client-core';
import { del, get, set } from 'idb-keyval';
import { persistedQueryIsStale } from './persisted-schemas';

const CLIENT_KEY = 'persisted-client';
const MAX_AGE_MS = 3 * 24 * 60 * 60 * 1000; // 3 days

// persistQueryClient subscribes to the query cache with no throttle of its own:
// every `added` / `removed` / `updated` event dehydrates the whole client and
// writes it to IndexedDB. A screenful of newly-mounted rows that each register a
// query turns into ~90 full-cache writes per scroll tick (~17-30ms of synchronous
// structuredClone + put each), which is what froze the order table. Coalescing
// bursts into one trailing write is safe: this is a cache, so losing the last
// second of it on a crash costs nothing.
export const PERSIST_DEBOUNCE_MS = 1000;

// Detect reload once at module load, before any async work.
// On hard refresh we skip restoring from IndexedDB so that TanStack Router
// loaders always fetch fresh data instead of short-circuiting with stale cache.
const isReload =
  (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined)
    ?.type === 'reload';

function isQueryExpired(query: {
  queryKey: readonly unknown[];
  state: { dataUpdatedAt: number };
}): boolean {
  return Date.now() - query.state.dataUpdatedAt > MAX_AGE_MS;
}

export function createIndexedDBPersister(): Persister {
  let persistDisabled = false;

  // Trailing-debounce state. Each call reschedules the timer, so only the last
  // client of a burst is ever written; chaining onto `lastWrite` keeps two
  // `set()` calls from overlapping when a later burst comes due mid-write.
  let flushTimer: ReturnType<typeof setTimeout> | undefined;
  let lastWrite: Promise<void> = Promise.resolve();

  async function writeClient(client: PersistedClient): Promise<void> {
    try {
      // Filter out expired queries before persisting
      if (client.clientState?.queries) {
        const validQueries = client.clientState.queries.filter((query) => {
          // Only persist queries that have data
          if (query.state.data === undefined) {
            return false;
          }

          // Check if expired
          return !isQueryExpired(query);
        });

        // Update client with only valid queries
        client.clientState.queries = validQueries;
      }

      await set(CLIENT_KEY, client);
    } catch (error) {
      console.warn('Failed to persist query client:', error);
      // Try to recover by clearing corrupted/full storage and retrying
      try {
        await del(CLIENT_KEY);
        await set(CLIENT_KEY, client);
      } catch {
        // Storage is fundamentally broken — disable persistence for this session
        console.warn('IndexedDB persistence disabled for this session due to repeated failures');
        persistDisabled = true;
      }
    }
  }

  function cancelPendingWrite(): void {
    clearTimeout(flushTimer);
    flushTimer = undefined;
  }

  return {
    persistClient(client: PersistedClient): Promise<void> {
      if (persistDisabled) {
        return Promise.resolve();
      }

      cancelPendingWrite();
      flushTimer = setTimeout(() => {
        flushTimer = undefined;
        lastWrite = lastWrite.then(() => (persistDisabled ? undefined : writeClient(client)));
      }, PERSIST_DEBOUNCE_MS);

      return Promise.resolve();
    },

    async restoreClient(): Promise<PersistedClient | undefined> {
      try {
        // On hard refresh, skip restore so route loaders fetch fresh data.
        // The persisted data stays in IndexedDB for future navigations.
        if (isReload) {
          return undefined;
        }

        const client = await get<PersistedClient>(CLIENT_KEY);

        if (!client) {
          return undefined;
        }

        // Drop expired queries, and any whose row schema has changed shape since
        // it was persisted — rehydration restores raw rows without re-running
        // them through Yup, so a stale-shape row would render (and can crash)
        // before the background refetch replaces it. Evicting just that query
        // forces a fresh, schema-valid fetch; the rest of the cache survives.
        if (client.clientState?.queries) {
          const validQueries = client.clientState.queries.filter(
            (query) => !isQueryExpired(query) && !persistedQueryIsStale(query.meta)
          );

          // Update client with only valid queries
          client.clientState.queries = validQueries;

          // If all queries expired, clear and return undefined
          if (validQueries.length === 0) {
            await del(CLIENT_KEY);

            return undefined;
          }
        }

        datadogRum.addAction('restore_query_client', {
          persist_bytes: JSON.stringify(client).length,
          persist_query_count: client.clientState?.queries?.length ?? 0,
        });

        return client;
      } catch (error) {
        console.warn('Failed to restore query client:', error);
        // Clear corrupted data so future persists can start fresh
        try {
          await del(CLIENT_KEY);
        } catch {
          persistDisabled = true;
        }

        return undefined;
      }
    },

    async removeClient(): Promise<void> {
      // Drop any debounced write first, otherwise it would resurrect the cache
      // we were just asked to delete.
      cancelPendingWrite();

      try {
        await del(CLIENT_KEY);
      } catch (error) {
        console.warn('Failed to remove query client:', error);
      }
    },
  };
}
